import { normalizeUrl } from '@vlabs/api/axios/base-client';
import { getLunaSampleUrl } from '@vlabs/api/axios/lp5-client';
import {
  getGetFacesQueryOptions, getGetFaceQueryOptions, getGetHandlersQueryOptions,
  getGetListsQueryOptions, getGetEventQueryOptions,
  matchFaces, matchBodies, sdk,
} from '@vlabs/api/luna-api/client';
import type {
  GetFacesParams, MatchBodiesRequestJson, MatchFacesRequestJson, MatchResult, SdkParams,
} from '@vlabs/api/luna-api/model';
import { queryClient } from '@vlabs/api/queryClient';
import { eventToEvent } from '@vlabs/api-bindings/src/luna-client/events/mappers';
import { schemes as facesSchemes } from '@vlabs/api-bindings/src/luna-client/faces/schemes';
import matcherSchemes from '@vlabs/api-bindings/src/luna-client/matcher/schemes';
import type { TMatchingResponse, TReference, TReferencePreview } from '@vlabs/monokit/entities/matcher/types/types';

import type { SearchMatchPayload } from './buildMatchPayload';
import type { SearchApi } from './SearchApi';

const LUNA_BASE_URL = import.meta.env.VITE_LP5_API_URL;

const getImageUrl = (url?: string): string | undefined => {
  if (!url) return undefined;
  if (url.startsWith('http') || url.startsWith('//')) return url;
  return normalizeUrl(`${LUNA_BASE_URL}${url}`);
};

const getAvatarUrl = (avatar?: string): string | undefined => {
  if (!avatar) return undefined;
  if (avatar.startsWith('http') || avatar.startsWith('//')) return avatar;
  return normalizeUrl(`${LUNA_BASE_URL}${avatar}`);
};

const lunaUrlClient = {
  images: { getURL: getImageUrl },
  samples: { getURL: getLunaSampleUrl },
  faces: { getAvatarURL: getAvatarUrl },
};

const readMatchResult = matcherSchemes.read(lunaUrlClient);
const readEvent = eventToEvent(lunaUrlClient);
const readFace = facesSchemes.read(lunaUrlClient);

const enrichMatchResponse = (response: MatchResult): TMatchingResponse[] => (
  response.map((matchResult) => readMatchResult(matchResult))
);

const FACE_ESTIMATE_PARAMS: SdkParams = {
  detect_face: 1,
  estimate_face_descriptor: 1,
  estimate_face_warp: 1,
};

const BODY_ESTIMATE_PARAMS: SdkParams = {
  detect_body: 1,
  estimate_body_descriptor: 1,
  estimate_body_warp: 1,
};

const estimate = async (file: File, params: SdkParams): Promise<unknown> => {
  const data = await sdk(file, params, { headers: { 'Content-Type': file.type } });
  return { data };
};

export const lunaSearchApi: SearchApi = {
  matchFaces: async (payload: SearchMatchPayload) => {
    const response = await matchFaces(payload as unknown as MatchFacesRequestJson);
    return enrichMatchResponse(response);
  },
  matchBodies: async (payload: SearchMatchPayload) => {
    const response = await matchBodies(payload as unknown as MatchBodiesRequestJson);
    return enrichMatchResponse(response);
  },
  getReferenceInfo: async (reference: TReference) => {
    const { id, face_id: faceId, type } = reference;

    if (type === 'event') {
      const event = await queryClient.fetchQuery(getGetEventQueryOptions(id));
      const eventPreview: TReferencePreview = readEvent(event);
      return [eventPreview];
    }

    if (type === 'face_external_id') {
      const params = { external_ids: [id], page: 1, page_size: 100 } as unknown as GetFacesParams;
      const response = await queryClient.fetchQuery(getGetFacesQueryOptions(params));
      return (response.faces ?? []).map(
        (face) => readFace(face) as unknown as TReferencePreview,
      );
    }

    if (type === 'face') {
      const face = await queryClient.fetchQuery(getGetFaceQueryOptions(faceId ?? id));
      const facePreview: TReferencePreview = readFace(face);
      return [facePreview];
    }

    return undefined;
  },
  estimateFace: async (file: File) => estimate(file, FACE_ESTIMATE_PARAMS),
  estimateBody: async (file: File) => estimate(file, BODY_ESTIMATE_PARAMS),
  getHandlerOptions: async () => {
    const handlers = await queryClient.fetchQuery(getGetHandlersQueryOptions());
    return handlers.map((handler) => ({ value: handler.handler_id, label: handler.description }));
  },
  getListOptions: async () => {
    const response = await queryClient.fetchQuery(getGetListsQueryOptions());
    return (response.lists ?? []).map((list) => ({ value: list.list_id, label: list.user_data }));
  },
  capabilities: {
    referenceTypes: ['sdk_descriptor', 'face_external_id', 'face', 'event'],
    faceCandidateFilters: [],
    eventCandidateFilters: [],
    requiredCandidateFilters: {},
  },
};
